import { useState, useEffect } from 'react';
import { useOutletContext, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Building2, 
  Users, 
  DollarSign, 
  TrendingUp,
  Copy,
  Check,
  ExternalLink,
  Plus,
  ArrowUpRight,
  AlertTriangle,
  Calendar
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { toast } from 'sonner';

import { API_BASE_URL } from '../utils/api';
const API_URL = API_BASE_URL;

export default function PartnerDashboard() {
  const { partner } = useOutletContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [copied, setCopied] = useState(false);
  const [upcomingPricing, setUpcomingPricing] = useState([]);

  useEffect(() => {
    fetchDashboard();
    fetchUpcomingPricing();
  }, []);

  const fetchUpcomingPricing = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/billing/pricing/upcoming`);
      setUpcomingPricing(response.data.upcoming || []);
    } catch (error) {
      console.error('Failed to fetch upcoming pricing:', error);
    }
  };

  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem('partner_token');
      if (!token) {
        navigate('/partner/login');
        return;
      }
      const response = await axios.get(`${API_URL}/api/partner/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000 // 30 second timeout
      });
      setDashboard(response.data);
    } catch (error) {
      console.error('Dashboard error:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
        // Don't auto-redirect to prevent loops
      } else if (error.code === 'ECONNABORTED') {
        // Retry once with longer timeout
        try {
          const token = localStorage.getItem('partner_token');
          const retryResponse = await axios.get(`${API_URL}/api/partner/dashboard`, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 60000
          });
          setDashboard(retryResponse.data);
          return;
        } catch (retryError) {
          toast.error('Dashboard is taking longer than expected. Please refresh.');
        }
      } else {
        toast.error('Failed to load dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = () => {
    if (dashboard?.referral_link) {
      navigator.clipboard.writeText(dashboard.referral_link);
      setCopied(true);
      toast.success('Referral link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NZ', {
      style: 'currency',
      currency: 'NZD'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground">Dashboard</h1>
        <p className="text-muted-foreground mt-1">
          Welcome back, {partner?.contact_name}
        </p>
      </div>

      {/* Upcoming Price Change Alert */}
      {upcomingPricing.length > 0 && (
        <Alert className="mb-6 bg-amber-500/10 border-amber-500/30">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          <AlertTitle className="text-amber-600 font-semibold flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Upcoming Price Change
          </AlertTitle>
          <AlertDescription className="text-amber-600/90">
            {upcomingPricing.map((version, idx) => {
              const effectiveDate = new Date(version.effective_date).toLocaleDateString('en-NZ', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              });
              return (
                <div key={idx}>
                  Your wholesale pricing will change on <strong>{effectiveDate}</strong>. 
                  New Pro plan rate: <strong>${version.prices?.pro?.wholesale_price_monthly || 0}/user/month</strong>.
                  <Link to="/partner/billing" className="ml-2 underline hover:no-underline">View details →</Link>
                </div>
              );
            })}
          </AlertDescription>
        </Alert>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Customers
            </CardTitle>
            <Building2 className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{dashboard?.summary?.total_companies || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">Companies referred</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Users
            </CardTitle>
            <Users className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{dashboard?.summary?.total_users || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">Across all customers</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Active Subscriptions
            </CardTitle>
            <TrendingUp className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{dashboard?.summary?.active_subscriptions || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">Paying customers</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border-cyan-500/30">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-cyan-600">
              Est. Monthly Cost
            </CardTitle>
            <DollarSign className="w-4 h-4 text-cyan-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-cyan-600">
              {formatCurrency(dashboard?.summary?.estimated_monthly_cost || 0)}
            </div>
            <p className="text-xs text-cyan-600/70 mt-1">{dashboard?.summary?.pro_users || 0} Pro users</p>
          </CardContent>
        </Card>
      </div>

      {/* Referral Link Card */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Your Referral Link</CardTitle>
          <CardDescription>
            Share this link with potential customers. When they sign up, they'll be attributed to your account.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="flex-1 bg-secondary/50 rounded-lg px-4 py-3 font-mono text-sm overflow-hidden">
              <span className="text-muted-foreground truncate block">
                {dashboard?.referral_link || 'Loading...'}
              </span>
            </div>
            <Button onClick={copyReferralLink} variant="outline">
              {copied ? (
                <>
                  <Check className="w-4 h-4 mr-2 text-green-500" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Link
                </>
              )}
            </Button>
          </div>
          <div className="mt-4 flex items-center gap-4 text-sm text-muted-foreground">
            <span>Referral Code: <code className="bg-secondary px-2 py-0.5 rounded">{dashboard?.referral_code}</code></span>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Customers */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Recent Customers</CardTitle>
              <CardDescription>Latest companies you've referred</CardDescription>
            </div>
            <Link to="/partner/customers">
              <Button variant="outline" size="sm">
                View All
                <ArrowUpRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {dashboard?.recent_companies?.length > 0 ? (
              <div className="space-y-4">
                {dashboard.recent_companies.map((company) => (
                  <div 
                    key={company.id} 
                    className="flex items-center justify-between p-3 rounded-lg bg-secondary/30 hover:bg-secondary/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">{company.name}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Users className="w-3 h-3" />
                          {company.user_count} users
                          <span className="mx-1">•</span>
                          <Badge variant="outline" className="text-xs capitalize">
                            {company.plan}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <Badge 
                      variant={company.subscription_status === 'active' ? 'default' : 'secondary'}
                      className={company.subscription_status === 'active' ? 'bg-green-500' : ''}
                    >
                      {company.subscription_status}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Building2 className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
                <p className="text-muted-foreground">No customers yet</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Share your referral link to get started
                </p>
                <Link to="/partner/customers">
                  <Button className="mt-4" size="sm">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Customer
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>Common tasks and resources</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to="/partner/customers" className="block">
              <Button variant="outline" className="w-full justify-start h-auto py-4">
                <Plus className="w-5 h-5 mr-3 text-primary" />
                <div className="text-left">
                  <p className="font-medium">Add New Customer</p>
                  <p className="text-xs text-muted-foreground">Create a subscription for a new customer</p>
                </div>
              </Button>
            </Link>
            
            <Link to="/partner/billing" className="block">
              <Button variant="outline" className="w-full justify-start h-auto py-4">
                <DollarSign className="w-5 h-5 mr-3 text-cyan-500" />
                <div className="text-left">
                  <p className="font-medium">View Billing</p>
                  <p className="text-xs text-muted-foreground">Check your costs and invoices</p>
                </div>
              </Button>
            </Link>

            <a href="https://hero.co.nz" target="_blank" rel="noopener noreferrer" className="block">
              <Button variant="outline" className="w-full justify-start h-auto py-4">
                <ExternalLink className="w-5 h-5 mr-3 text-blue-500" />
                <div className="text-left">
                  <p className="font-medium">Hero Partner Portal</p>
                  <p className="text-xs text-muted-foreground">Access Hero PBX management</p>
                </div>
              </Button>
            </a>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
